import {reducer, useSetStateReducer} from '../../../Custom Hooks/useMemorizedReducer/useMemorized.reducer';
import {initializeState} from '../Config/AutoProject.state';
import { useLocation, useHistory } from 'react-router-dom';
import {useEffect, useState} from 'react';
import {request} from '../../../services/HttpRequestService';
import {storageService} from '../../../services/storageService';
import useUploadProcess from '../../../Custom Hooks/useUploadProcess/useUploadProcess';
import useAutoProjectImageCollectionTab from '../../../Containers Components/AutoProjectImageCollectionTab/Controller/AutoProjectImageCollectionTab.controller';
import {ClassFlowPhase, StatusMessageBox, TrainingTypes, ProjectFlowPhase} from '../Config/AutoProject.constants';
import {IPartialAutoProjectState} from '../Config/AutoProject.types';
import useAutoProjectTrainingStatus from '../../../Containers Components/AutoProjectTrainingStatusTab/Controller/AutoProjectTrainingStatusTab.controller';
import useBowserNavigation from '../../../Custom Hooks/useBowserNavigation/useBowserNavigation';
import {_SYSTEM_TITLES} from '../../../configuration/SystemTitles/SystemTitles.config';
import {FLOW} from '../../RandomSampler/Config/RandomSampler.constants';

const useAutoProject = () => {
    const [getState, setState] = useSetStateReducer(reducer, initializeState);
    const autoProjectState = getState();
    const location = useLocation();
    const history = useHistory();
    const {state} = location;
    const {projectIdInit} = state || {};
    const [openStopModal, setOpenStopModal] = useState(false);


    const {fileBoilerPlate} = useUploadProcess(-1, -1, true);
    const {updateProjectMessageStatus} = useAutoProjectImageCollectionTab();
    const {initTrainingsData} = useAutoProjectTrainingStatus({projectIdInit, setState});

    /**
     * navigateToDataProjects - navigate to the page of all projects
     * */
    const navigateToDataProjects = () => {
        history.push({pathname: `/${_SYSTEM_TITLES.projects}`, state: {}});
    };

    /**
     * getProjectData - get from the server the project data by project id
     * @param projectId - the id of the current project
    * */
    const getProjectData = async (projectId) => {
        try {
            const res = await request.get(storageService.loadFromStorage('token'), 'GET_PROJECT_DETAILS', false, projectId);
            const {classes, tags, id, name, description} = res.data;
            const projectState = {
                projectName: name,
                classes: tags.map((t) => ({...t, imageList: classes[t.tag_id].map((file, index) => fileBoilerPlate(file, index))})),
                projectId: id,
                projectDescription: description
            };

            return projectState;
        } catch (e) {
            console.log(e);
        }
    };

    useBowserNavigation(navigateToDataProjects, null, null, true);

    /**
     * getAutoProjectStatus - get from the server the automated project status project classes statuses
     * @param projectId - the id of the current project
     * */
    const getAutoProjectStatus = async (projectId) => {
        try {
            const res = await request.get(storageService.loadFromStorage('token'), 'GET_AUTO_PROJECT_STATUS', false, projectId);
            const {creationTimestamp, class_training_goal, classesStatus, flow_phase,
                current_batch_good_counter, sniffer_batch_goal, classesInTraining, trainingStatus, thresholdPercent} = res.data;

            const newStatusState = {
                startedDate: formatDate(creationTimestamp),
                trainingGoal: class_training_goal,
                currentBatchGoodCounter: current_batch_good_counter,
                snifferBatchGoal: sniffer_batch_goal,
                classesStatus,
                projectFlowPhase: flow_phase,
                classesInTraining,
                trainingStatus,
                thresholdPercent
            };

            return newStatusState as any;
        } catch (e) {
            console.log(e);
        }
    };

    /**
     * formatDate - reformat the date for needed format
     * @param timestamp - the date in timestamp
     * */
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
        const day = date.getDate();
        const year = date.getFullYear();
        const hour = date.getHours() % 12 || 12;
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const amPm = date.getHours() >= 12 ? 'PM' : 'AM';


        return `${month}${day} ${year} ${hour}:${minutes}${amPm}`;
    };

    /**
     * updateClassesStatus - update all classes status
     * @param newClassesStatus - the new statuses
     * @param classesInTraining - array that represents the classes that in training
     * @param trainingStatus - the status of the training
     * @param projectState - the update project data from the server
     * */
    const updateClassesStatus = (newClassesStatus, classesInTraining, trainingStatus, projectState) => {
        for (const oldClass of projectState['classes']) {
            newClassesStatus && newClassesStatus.forEach((updatedClass) => {
                if (updatedClass['project_class'] === oldClass['tag_id']) {
                    oldClass['flowPhase'] = updatedClass['class_flow_phase'];
                    oldClass['readyImagesCounter'] = updatedClass['images_ready_for_training'];
                    oldClass['classClusterId'] = updatedClass['id'];
                    oldClass['isError'] = updatedClass['is_error'];
                    oldClass['manualRefinementsCounter'] = updatedClass['manual_refinements_counter'];
                    if (updatedClass['class_flow_phase'] === ClassFlowPhase.TrainingReady) {
                        if (classesInTraining && classesInTraining.length > 0) {
                            const is_class_in_training = classesInTraining.includes(oldClass['tag_id']);

                            if (is_class_in_training) {
                                oldClass['trainingStatus'] = trainingStatus;
                            }
                        }
                    }
                }
            });
        }

        return projectState;
    };

    /**
     * initialClassesStatusBeforeClusteringStarted - initial classes status to be at flow phase 0
     * and with 0 readyImages because clustering not started yet
     * @param projectState - project current state
     * */
    const initialClassesStatusBeforeClusteringStarted = (projectState) => {
        projectState['classes'] && projectState['classes'].forEach((curClass) => {
            curClass['flowPhase'] = ClassFlowPhase.InCluster;
            curClass['readyImagesCounter'] = 0;
            curClass['isError'] = false;
            curClass['manualRefinementsCounter'] = 0;
        });

        return projectState;
    };


    /**
     * goToManualRefinement - when user want to go to Manual Refinement
     */
    const goToManualRefinement = () => {
        history.push({
            pathname: '/auto_classification_manual_refinement',
            state: {classes: autoProjectState.classes, projectIdInit, flow: FLOW.autoProject, batchId: -1},
        });
    };

    /**
     * convertTagsToAIQL - convert the tags to AIQL format and return classes that
     * not include in the training
     * */
    const convertTagsToAIQL = () => {
        const AIQL = {};
        const classesToTrain = autoProjectState.classes.filter((curClass) => curClass.flowPhase === ClassFlowPhase.TrainingReady);
        const classesOutsideTraining = autoProjectState.classes.filter((curClass) => curClass.flowPhase !== ClassFlowPhase.TrainingReady);

        classesToTrain.forEach((curClass, index) => {

            const query = {
                labels: [],
                attribute: []
            };

            query['tag'] = [curClass.tag_id];
            AIQL[`Q${index + 1}`] = query;
        });

        return {AIQL, classesOutsideTraining};
    };


    /**
     * UpdateClassesOutsideTraining - update classes that are not part
     * of the training to be in flow phase to on hold
     * @param classesOutsideTraining - classes array that are not part of the training
     * */
    const UpdateClassesOutsideTraining = (classesOutsideTraining) => {
        const newClassesState = JSON.parse(JSON.stringify(autoProjectState.classes));
        const classStateIndex = {};

        for (const curClassFromState of newClassesState) {
            classStateIndex[curClassFromState.tag_id] = curClassFromState;
        }
        for (const curClass of classesOutsideTraining) {
            const classFromState = classStateIndex[curClass.tag_id];

            if (classFromState) {
                classFromState.flowPhase = ClassFlowPhase.OnHold;
            }
        }

        return newClassesState;
    };

    /**
     * startTrainingBtn - send a request for execute training
     * */
    const startTrainingBtn = async () => {
        try {
            const {AIQL, classesOutsideTraining} = convertTagsToAIQL();
            const res = await request.post(storageService.loadFromStorage('token'), 'START_TRAINING', {
                data_project_id: autoProjectState.projectId,
                feature_name: autoProjectState.projectName,
                training_type: TrainingTypes.Classification,
                AIQL
            });
            const {training_id} = res.data;
            const newClassesState = UpdateClassesOutsideTraining(classesOutsideTraining);

            setState({isTrainingStarted: true, trainingId: training_id, classes: newClassesState});

        } catch (e) {
            console.log(e);
        }
    };

    /**
     * initClassesStatus - arranges and updates the classes status data from the new server data
     * returns the new object for updating the state
     * @param projectState - the current project state
     * @param newStatusState - the new data from server
     * */
    const initClassesStatus = (projectState, newStatusState) => {
        let newProjectState: IPartialAutoProjectState = {};

        if (newStatusState.classesStatus.length === 0) {
            newProjectState = initialClassesStatusBeforeClusteringStarted(projectState);
        } else {
            const {trainingStatus, classesInTraining} = newStatusState;

            newProjectState = updateClassesStatus(newStatusState.classesStatus, classesInTraining, trainingStatus, projectState);
            delete newStatusState.classesStatus;
        }

        return newProjectState;
    };

    /**
     * stopAutoProject - send request to the server in to stop auto project
     * update the classes and the project flow phase states
     * */
    const stopAutoProject = async () => {
        try {
            await request.post(storageService.loadFromStorage('token'), 'STOP_AUTO_PROJECT', {
                data_project_id: autoProjectState.projectId
            });
            setOpenStopModal(false);
            setState({projectFlowPhase: ProjectFlowPhase.Stopped, messageBoxFlowPhase: StatusMessageBox.ProjectStopped});
        } catch (e) {
            console.log(e);
        }

    };

    useEffect(() => {
        if (projectIdInit > -1) {
            (async () => {
                const [projectState, newStatusState] = await Promise.all([getProjectData(projectIdInit), getAutoProjectStatus(projectIdInit)]);

                const newProjectState = initClassesStatus(projectState, newStatusState);

                let messageBoxFlowPhase = updateProjectMessageStatus(newProjectState && newProjectState.classes);

                if (newStatusState.projectFlowPhase === ProjectFlowPhase.Stopped) {
                    messageBoxFlowPhase = StatusMessageBox.ProjectStopped;
                }
                if (newStatusState.projectFlowPhase === ProjectFlowPhase.Error) {
                    messageBoxFlowPhase = StatusMessageBox.ProjectError;
                }
                const newState = {...newProjectState, ...newStatusState, messageBoxFlowPhase};
                let trainingState = {};

                if (newStatusState.projectFlowPhase === ProjectFlowPhase.Training) {
                    trainingState = await initTrainingsData(projectIdInit) as any;
                }
                if (Object.keys(trainingState).length !== 0) {

                    setState({...newState, ...trainingState, isTrainingStarted: true});
                } else {
                    setState({...newState});
                }
            })();
        }
    }, []);

    return {autoProjectState, navigateToDataProjects, setState, getProjectData,
        getAutoProjectStatus, goToManualRefinement, startTrainingBtn,
        projectIdInit, formatDate, stopAutoProject, openStopModal, setOpenStopModal};

};

export default useAutoProject;
